import React, { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { X } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/env';
import { cn } from '../../utils/cn';

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

interface ChangePasswordData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordResponse {
  success: boolean;
  data: any;
  expandedError?: {
    errorCode: number;
    message: string;
    technicalMessage: string | null;
  };
}

type ReqKey = 'len' | 'lower' | 'upper' | 'num' | 'special';

const REQUIREMENT_CHECKS: Record<ReqKey, (p: string) => boolean> = {
  len: (p) => p.length >= 8,
  lower: (p) => /[a-z]/.test(p),
  upper: (p) => /[A-Z]/.test(p),
  num: (p) => /\d/.test(p),
  special: (p) => /[^a-zA-Z0-9]/.test(p),
};

const REQUIREMENT_ROWS: { key: ReqKey; col: 1 | 2; label: string }[] = [
  { key: 'len', col: 1, label: '8 or more characters' },
  { key: 'lower', col: 1, label: 'One lowercase letter' },
  { key: 'special', col: 1, label: 'One special character' },
  { key: 'upper', col: 2, label: 'One uppercase letter' },
  { key: 'num', col: 2, label: 'One number' },
];

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ show, onHide }) => {
  const [formData, setFormData] = useState<ChangePasswordData>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push('Minimum 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('At least one lowercase letter');
    if (!/\d/.test(password)) errors.push('At least one digit');
    if (!/[^a-zA-Z0-9]/.test(password)) errors.push('At least one special character');
    return errors;
  };

  const handleInputChange = (field: keyof ChangePasswordData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.oldPassword) {
        setError('Please enter your current password');
        return;
      }

      if (!formData.newPassword) {
        setError('Please enter a new password');
        return;
      }

      if (!formData.confirmPassword) {
        setError('Please confirm your new password');
        return;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        setError('New password and confirm password do not match');
        return;
      }

      const passwordErrors = validatePassword(formData.newPassword);
      if (passwordErrors.length > 0) {
        setError(`Password does not meet the required criteria: ${passwordErrors.join(', ')}`);
        return;
      }

      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await fetch(API_ENDPOINTS.AUTH_CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
        }),
      });

      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0' || !response.body) {
          setSuccess('Password changed successfully!');
          setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
          setTimeout(() => onHide(), 2000);
        } else {
          try {
            const data: ChangePasswordResponse = await response.json();
            if (data.success) {
              setSuccess('Password changed successfully!');
              setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
              setTimeout(() => onHide(), 2000);
            } else {
              setError(data.expandedError?.message || 'Failed to change password');
            }
          } catch {
            setSuccess('Password changed successfully!');
            setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
            setTimeout(() => onHide(), 2000);
          }
        }
      } else {
        try {
          const data: ChangePasswordResponse = await response.json();
          setError(data.expandedError?.message || 'Failed to change password');
        } catch {
          setError(`Failed to change password (${response.status})`);
        }
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setError('An error occurred while changing password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setError(null);
    setSuccess(null);
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onHide();
  };

  const passwordErrors = formData.newPassword ? validatePassword(formData.newPassword) : [];

  const inputClass =
    'w-full rounded-xl border border-[#d1d9e0] bg-white py-3 pl-3 pr-[4.5rem] text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/20';
  const labelClass = 'mb-2 block text-sm font-semibold text-slate-800';

  const renderPasswordField = (
    id: string,
    label: string,
    value: string,
    field: keyof ChangePasswordData,
    visible: boolean,
    setVisible: (v: boolean) => void,
    placeholder: string,
    helper?: React.ReactNode
  ) => (
    <div className="mb-5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={field === 'oldPassword' ? 'current-password' : 'new-password'}
          className={inputClass}
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible(!visible)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#2297D1] hover:text-[#1b7aad]"
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
      {helper}
    </div>
  );

  const col1 = REQUIREMENT_ROWS.filter((r) => r.col === 1);
  const col2 = REQUIREMENT_ROWS.filter((r) => r.col === 2);

  const renderRequirement = (key: ReqKey, label: string) => {
    const met = formData.newPassword.length > 0 && REQUIREMENT_CHECKS[key](formData.newPassword);
    return (
      <div key={key} className="flex gap-2">
        <span className="shrink-0 pt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">
          {met ? 'Done' : 'Needed'}
        </span>
        <span className={cn('text-sm', met ? 'font-medium text-emerald-700' : 'text-slate-600')}>{label}</span>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      contentClassName="border-0 rounded-2xl shadow-2xl overflow-hidden"
      dialogClassName="modal-dialog-centered mx-auto w-full !max-w-5xl px-2"
    >
      <div className="bg-white">
        <div className="relative border-b border-slate-100 px-6 pb-4 pt-6 sm:px-8">
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Account security</p>
          <h2 className="mb-2 pr-10 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Update your password</h2>
          <p className="mb-0 max-w-lg text-sm leading-relaxed text-slate-600">
            Use a unique password for this workspace so your team access stays protected.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-5 sm:px-8">
          <div className="mb-6 rounded-xl border border-[#b8d9f0] bg-[#eff8ff] px-4 py-3">
            <p className="mb-1 text-sm font-semibold text-slate-900">Quick check</p>
            <p className="mb-0 text-sm leading-relaxed text-slate-700">
              Keep this password private, avoid reusing it elsewhere, and update any saved browser password once you are done.
            </p>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
          )}

          {success && (
            <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {success}
            </div>
          )}

          {renderPasswordField(
            'cp-old',
            'Current password',
            formData.oldPassword,
            'oldPassword',
            showOldPassword,
            setShowOldPassword,
            'Write your current password'
          )}

          {renderPasswordField(
            'cp-new',
            'New password',
            formData.newPassword,
            'newPassword',
            showNewPassword,
            setShowNewPassword,
            'Choose your new password'
          )}

          <div className="mb-5 grid gap-4 sm:grid-cols-2 sm:gap-8">
            <div className="space-y-3">{col1.map((r) => renderRequirement(r.key, r.label))}</div>
            <div className="space-y-3">{col2.map((r) => renderRequirement(r.key, r.label))}</div>
          </div>

          {renderPasswordField(
            'cp-confirm',
            'Confirm new password',
            formData.confirmPassword,
            'confirmPassword',
            showConfirmPassword,
            setShowConfirmPassword,
            'Re-enter your new password',
            <p className="mt-2 text-xs text-slate-500">Re-enter the password once more to confirm it.</p>
          )}

          {formData.confirmPassword.length > 0 && formData.newPassword !== formData.confirmPassword && (
            <p className="-mt-3 mb-5 text-xs font-medium text-red-600">Passwords do not match</p>
          )}

          <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="rounded-xl border border-[#c5cdd6] bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formData.oldPassword ||
                !formData.newPassword ||
                !formData.confirmPassword ||
                passwordErrors.length > 0
              }
              className="rounded-xl bg-[#2297D1] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1b7aad] disabled:opacity-50"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner animation="border" size="sm" className="!h-4 !w-4 border-white" />
                  Updating…
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
